import { Loading, TableCard, TableOutput } from "@general-backoffice/core";
import { FormInput } from "@general-backoffice/core/index";
import type { PaginationMeta, TotalChargesData, UserChargesData } from "@investhinks/api-client";
import classNames from "classnames";
import moment from "moment";
import React from "react";
import { useTranslation } from "react-i18next";
import { Card, FormGroup } from "reactstrap";

import { TypedTable } from "../../utils/typedLib";
import PaginatedTableFooter from "../shared/PaginatedTableFooter";

const STARTING_YEAR = 2023;
const FINAL_YEAR = Number(moment().format("YYYY"));
const years: number[] = [];
for (let y = STARTING_YEAR; y <= FINAL_YEAR; y++) {
  years.push(y);
}

interface YearChargesListCardProps {
  perUser?: UserChargesData[];
  total?: TotalChargesData;
  onUserClick?: (row: { id?: number | string }) => void;
  isLoading?: boolean;
  pagination?: PaginationMeta;
  goToPage?: (page: number) => void;
  changePageSize?: (size: number) => void;
  year?: string | number;
  setYear?: (year: string | number) => void;
}

const isUserChargesData = (row: TotalChargesData | UserChargesData): row is UserChargesData => "email" in row;

const YearChargesListCard: React.FC<YearChargesListCardProps> = ({
  perUser = [],
  total = { perMonth: {}, totalCharged: 0, totalPending: 0 },
  onUserClick,
  isLoading,
  pagination,
  goToPage,
  changePageSize,
  year,
  setYear,
}) => {
  const { t } = useTranslation("adminStats", { keyPrefix: "yearCharges" });
  // Combined row type - first row is total (no email), rest are users
  type TableRow = TotalChargesData | UserChargesData;

  const columns = [
    {
      compressed: true,
      name: t("columns.user"),
      formattedContent: (row: TableRow, i: number) =>
        !i ? (
          <span className="text-uppercase h3 ">{t("total")}</span>
        ) : (
          <>
            <div>{isUserChargesData(row) ? row.name : ""}</div>
            <small className="font-weight-bold">{isUserChargesData(row) ? row.email : ""}</small>
          </>
        ),
      isTh: true,
    },
    {
      compressed: true,
      formattedContent: (row: TableRow) => {
        if (!isUserChargesData(row)) return null;
        const { email, isVIP, role, blocked } = row;
        const bookmarks: Parameters<typeof TableOutput.Bookmarks>[0]["bookmarks"] = [];
        if (!email) return null;
        if (isVIP) {
          bookmarks.push({
            icon: "VIP",
            title: "VIP",
            color: "warning",
          });
        }
        if (role?.name !== "Authenticated") {
          bookmarks.push({
            icon: <i className="fas fa-user-times" />,
            title: "User not verified",
            color: "danger",
          });
        }
        if (blocked) {
          bookmarks.push({
            icon: <i className="fas fa-lock" />,
            title: "User blocked",
            color: "danger",
          });
        }
        return <TableOutput.Bookmarks bookmarks={bookmarks} />;
      },
    },
    {
      name: "Total",
      formattedContent: (row: TableRow, i: number) => (
        <span className={classNames("text-monospace", { "h3 text-primary": !i })}>
          {row.totalCharged?.toFixed(2) || "--.--"} €
        </span>
      ),
      isTh: true,
    },
    ...Object.keys(total?.perMonth || {}).map((k) => {
      const month = moment.utc(k, "M").format("MMM");
      return {
        name: month,
        formattedContent: (row: TableRow, i: number) => (
          <span className={classNames("text-monospace", { "h4 text-default": !i })}>
            {row.perMonth?.[k]?.charged?.toFixed(2) || "--.--"} €
          </span>
        ),
      };
    }),
  ];

  return (
    <Card className="overflow-hidden wide-table-card">
      <TableCard.Header title={t("title")} subtitle={String(year)}>
        <div className="flex-center justify-content-end flex-wrap" style={{ gap: ".8rem 1rem" }}>
          <FormGroup className="mb--4" style={{ minWidth: 120 }}>
            <FormInput.Select
              placeholder={t("selectYear")}
              onChange={(opt: { value: string | number } | null) => setYear?.(opt?.value ?? "")}
              value={{ value: year, label: year }}
              options={years.map((y) => ({ value: y, label: y }))}
              isDisabled={isLoading}
              isClearable={false}
            />
          </FormGroup>
        </div>
      </TableCard.Header>
      {isLoading && <Loading.Line />}
      <TypedTable
        columns={columns}
        rows={[total, ...perUser]}
        isLoading={isLoading}
        noContentMessage={t("noContent")}
        rowAmount={pagination?.pageSize ?? 10}
        // The totals row is injected at index 0 and has no user, so clicking it
        // used to call onUserClick with an undefined id.
        onRowClick={(row: TableRow) => {
          if (isUserChargesData(row)) onUserClick?.(row);
        }}
        selectable={false}
        sortable={false}
      />
      {goToPage && changePageSize && (
        <PaginatedTableFooter
          isLoading={!!isLoading}
          pagination={pagination}
          goToPage={goToPage}
          changePageSize={changePageSize}
        />
      )}
    </Card>
  );
};

export default YearChargesListCard;
